import {api} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import * as Constants from "@/shared/constants";
import {FieldObserver} from "@/shared/ui/fieldObserver";
import {htmlEscape} from "@/shared/utils/string";
import {
	HolidayCalculator,
	HolidayIndividualDayChangeDetail,
	HolidayIndividualDays,
	IndividualHoliday
} from "@/features/holidays/holidayIndividualDays";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {copyOfObject} from "@/shared/utils/misc";
import {formatDate, formatHalfDay, getISODate, parseDateInput, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {Holiday, HolidayCalendar, HolidayGenerationDiffResponse} from "@/shared/timr";
import {HolidayCalendarGenerationMode, HolidayDayDuration} from "@/shared/timrTypes";

export class HolidaysAdministrationPage {
	private readonly table: HolidaysTable;
	private readonly holidayDialogHandler: HolidayDialogHandler;
	private readonly holidayCalenderDialogHandler: HolidayCalendarDialogHandler;
	private readonly calculateHolidayDialogHandler: CalculateHolidayDialogHandler;

	constructor() {
		this.table = new HolidaysTable({
			element: $("#holidaysTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			initialFilter: this.buildTableFilterData()
		});

		this.holidayDialogHandler = new HolidayDialogHandler();
		$(this.holidayDialogHandler)
			.on(HolidayDialogHandler.EVENT_HOLIDAY_SAVED, () => {
				this.updateTable();
			})
			.on(HolidayDialogHandler.EVENT_HOLIDAY_DELETE, (event, holiday: Holiday, button: JQuery) => {
				this.deleteHoliday(holiday, button);
			});

		this.holidayCalenderDialogHandler = new HolidayCalendarDialogHandler();
		$(this.holidayCalenderDialogHandler)
			.on(HolidayCalendarDialogHandler.EVENT_HOLIDAY_CALENDAR_SAVED, (event, calendar: HolidayCalendar) => {
				this.updateHolidayCalendarsList(calendar.id);
			})
			.on(HolidayCalendarDialogHandler.EVENT_HOLIDAY_CALENDAR_DELETE, (event, calendar: HolidayCalendar, button: JQuery) => {
				this.deleteHolidayCalendar(calendar, button);
			});

		this.calculateHolidayDialogHandler = new CalculateHolidayDialogHandler();
		this.calculateHolidayDialogHandler.onHolidaysCalculated(() => {
			this.updateTable();
		});

		this.updateHolidayCalendarsList();

		$("#holidayCalendars").change(() => {
			this.updateTablePanelHeader();
			this.updateTable();
		});

		$(this.table)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + HolidaysTable.EVENT_HOLIDAY_EDIT, (event, holiday: Holiday) => {
				if (this.getSelectedHolidayCalendar().generationMode == HolidayCalendarGenerationMode.ON) {
					// Don't allow manual editing if the automatic generation mode is ON
					// Only needed for EVENT_DBLCLICK as the edit context menu entry is hidden in this case
					return;
				}

				this.holidayDialogHandler.setHoliday(holiday);
				this.holidayDialogHandler.open();
			})
			.on(HolidaysTable.EVENT_HOLIDAY_DELETE, (event, holiday: Holiday) => {
				this.deleteHoliday(holiday);
			});

		$("#holidaysAddButton").click(() => {
			this.holidayDialogHandler.resetHoliday();
			this.holidayDialogHandler.open();
		});

		$("#addHolidayCalendarButton").click(() => {
			this.holidayCalenderDialogHandler.resetHolidayCalendar();
			this.holidayCalenderDialogHandler.open();
		});

		$("#editHolidayCalendarButton").click(() => {
			this.holidayCalenderDialogHandler.setHolidayCalendar(this.getSelectedHolidayCalendar());
			this.holidayCalenderDialogHandler.open();
		});

		$("#calculateHolidaysButton").click(() => {
			this.calculateHolidayDialogHandler.calculateHolidaysFor(this.getSelectedHolidayCalendar());
		});
	}

	private updateHolidayCalendarsList(selectedId?: number) {
		const holidayCalendarsSelect = $("#holidayCalendars");
		const selectedHolidayCalendar = selectedId || (holidayCalendarsSelect.val() as string);
		holidayCalendarsSelect.empty();

		api("admin/holidayCalendars.json")
			.get()
			.done((responseData) => {
				if (responseData?.holidayCalendars) {
					responseData.holidayCalendars.forEach(holidayCalendar => {
						holidayCalendarsSelect
							.append($("<option></option>", {value: holidayCalendar.id})
								.text(holidayCalendar.description)
								.data("holidayCalendar", holidayCalendar));
					});

					if (selectedHolidayCalendar && holidayCalendarsSelect.find("option[value='" + selectedHolidayCalendar + "']").length !== 0) {
						holidayCalendarsSelect.val(selectedHolidayCalendar);
					}

					this.updateTable();
					this.updateTablePanelHeader();
				}
			});
	}

	private deleteHolidayCalendar(calendar: HolidayCalendar, initiatingButton?: JQuery) {
		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: messages.withArguments("dialog.delete.holidayCalendar.message", calendar.description),
			yesLabel: "confirmation.delete",
			yesAction: modal => {
				api("admin/deleteHolidayCalendar.json")
					.post({
						holidayCalendarId: calendar.id
					})
					.done(() => {
						this.updateHolidayCalendarsList();
						this.holidayCalenderDialogHandler.close();
					})
					.always(() => {
						Button.resetLoading(initiatingButton);
						modal.close();
					});
			},
			noAction: modal => {
				modal.close();
				Button.resetLoading(initiatingButton);
			}
		});
	}

	private updateTable() {
		const holidayCalendar = this.getSelectedHolidayCalendar();
		this.table.setAllowManualEdit(holidayCalendar.generationMode !== HolidayCalendarGenerationMode.ON);
		this.table.filter(this.buildTableFilterData());
	}

	private updateTablePanelHeader() {
		const holidayCalendar = this.getSelectedHolidayCalendar();
		$("#holidayCalendarTitle").text(holidayCalendar.description);
		$("#calculateHolidaysButton, #holidaysAddButton").toggle(holidayCalendar.generationMode !== HolidayCalendarGenerationMode.ON)
	}

	private getSelectedHolidayCalendar(): HolidayCalendar {
		return $("#holidayCalendars").find("option:selected").data("holidayCalendar") as HolidayCalendar;
	}

	private deleteHoliday(holiday: Holiday, initiatingButton?: JQuery) {
		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: messages.withArguments("dialog.delete.holiday.message", holiday.description),
			yesLabel: "confirmation.delete",
			yesAction: modal => {
				api("admin/deleteHoliday.json")
					.post({
						id: holiday.id
					})
					.done(() => {
						this.table.reload();
						this.holidayDialogHandler.close();
					})
					.always(() => {
						modal.close();
						Button.resetLoading(initiatingButton);
					});
			},
			noAction: modal => {
				modal.close();
				Button.resetLoading(initiatingButton);
			}
		});
	}

	private buildTableFilterData() {
		return [
			{
				name: "holidayCalendarId",
				value: $("#holidayCalendars").val() as string
			}
		];
	}
}

class HolidaysTable extends BasicDataTable<Holiday> {
	public static EVENT_HOLIDAY_EDIT = "eventHolidayEdit";
	public static EVENT_HOLIDAY_DELETE = "eventHolidayDelete";

	private allowManualEdit = true;

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/holidays.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			deferLoading: 0,
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<Holiday>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "date", orderable: true, render: (d, t, r, m) => this.renderHolidayDate(d, t, r, m)},
			{data: "description", orderable: true, render: BasicDataTable.renderText},
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "holidays.context.edit",
				action: holiday => { $(this).triggerHandler(HolidaysTable.EVENT_HOLIDAY_EDIT, holiday); }
			})
			.addMenuEntry({
				label: "holidays.context.delete",
				action: holiday => { $(this).triggerHandler(HolidaysTable.EVENT_HOLIDAY_DELETE, holiday); }
			})
			.onOpenByRightClick(() => this.allowManualEdit);
	}

	private renderHolidayDate(data: string, type: string, recording: Holiday, meta: DataTables.CellMetaSettings) {
		return BasicDataTable.renderText(data, type, recording, meta) + formatHalfDay(recording.halfDay);
	}

	public setAllowManualEdit(allow: boolean) {
		this.dataTable.column(0).visible(allow);
		this.allowManualEdit = allow;
	}
}

class HolidayDialogHandler {
	public static EVENT_HOLIDAY_SAVED = "eventHolidaySaved";
	public static EVENT_HOLIDAY_DELETE = "eventHolidayDelete";

	private readonly modal: BootstrapModal.Modal;
	private holiday: Holiday;

	constructor() {
		this.modal = new BootstrapModal.Modal({
			title: "holiday.header.create",
			message: $("#holidayDialog"),
			destroyOnClose: false,
			buttons: [
				{
					text: "dialog.save",
					action: this.saveHoliday
				},
				{
					text: "dialog.cancel"
				},
				{
					text: "dialog.delete",
					css: "btn-outline-danger",
					left: true,
					action: (modal, button) => { $(this).triggerHandler(HolidayDialogHandler.EVENT_HOLIDAY_DELETE, [this.holiday, button]); }
				}
			],
			onOpen: () => {
				$("#holidayDate").focus();
			}
		});
	}

	public resetHoliday() {
		this.setHoliday({
			id: null,
			date: formatDate(Temporal.Now.plainDateISO()),
			description: "",
			halfDay: false,
			version: null,
		});
	}

	public setHoliday(holiday: Holiday) {
		this.holiday = holiday;
		this.updateTitle();
		this.updateFieldValues();
	}

	private updateFieldValues() {
		this.modal.toggleButton("btn-outline-danger", this.holiday.id != null);
		$("#holidayId").val(this.holiday.id);
		$("#holidayHolidayCalendarId").val($("#holidayCalendars").val());
		$("#holidayDate").val(this.holiday.date);
		$("#holidayDescription").val(this.holiday.description);
		$(`#holidayHalfDayRadios input[value="${this.holiday.halfDay}"]`)
			.prop("checked", true);
		$("#holidayVersion").val(this.holiday.version);
	}

	public open() {
		this.modal.show();
		$("#holidayDate").focus();
	}

	public close() {
		this.modal.close();
	}

	private saveHoliday = (modal: BootstrapModal.Modal, button: JQuery) => {
		Button.setLoading(button);

		const holiday = $("#holidayDate").val() as string;

		if (holiday) {
			$("#holidayDateConverted").val(getISODate(parseDateInput(holiday)));
		} else {
			$("#holidayDateConverted").val("");
		}

		api("admin/saveHoliday.json")
			.postForm("#holidayForm")
			.done(() => {
				modal.close();

				$(this).triggerHandler(HolidayDialogHandler.EVENT_HOLIDAY_SAVED);
			})
			.always(() => {
				Button.resetLoading(button);
			});
	}

	private updateTitle() {
		const message = messages[this.holiday.id == null ? "holiday.header.create" : "holiday.header.edit"];

		this.modal.setTitle(message, true);
	}
}


class HolidayCalendarDialogHandler {
	public static EVENT_HOLIDAY_CALENDAR_SAVED = "eventHolidayCalendarSaved";
	public static EVENT_HOLIDAY_CALENDAR_DELETE = "eventHolidayCalendarDelete";

	private modal: BootstrapModal.Modal;
	private holidayCalendar: HolidayCalendar;
	private originalCalendarState: HolidayCalendar;
	private readonly fieldObserver: FieldObserver<HolidayCalendar> = new FieldObserver();
	private readonly holidayIndividualDays: HolidayIndividualDays | null = null;
	private calculatorInfo: HolidayCalculator | null = null;

	constructor() {
		this.modal = new BootstrapModal.Modal({
			title: "holidayCalendar.header.create",
			message: $("#holidayCalendarDialog"),
			destroyOnClose: false,
			buttons: [
				{
					text: "dialog.save",
					action: this.saveHolidayCalendar
				},
				{
					text: "dialog.cancel"
				},
				{
					text: "dialog.delete",
					css: "btn-outline-danger",
					left: true,
					action: (modal, button) => { $(this).triggerHandler(HolidayCalendarDialogHandler.EVENT_HOLIDAY_CALENDAR_DELETE, [this.holidayCalendar, button]); }
				}
			],
			onOpen: () => {
				$("#holidayCalendarDescription").focus();
			}
		});

		this.setupEventHandlers();
		this.holidayIndividualDays = document.getElementById("holidayCalendarIndividualHolidays") as HolidayIndividualDays;
		this.holidayIndividualDays?.addEventListener(HolidayIndividualDays.EVENT_DAY_SELECTION_CHANGE, e => {
			if (!this.holidayCalendar) {
				return;
			}

			const customEvent = e as CustomEvent<HolidayIndividualDayChangeDetail>;
			if (customEvent.detail.type === IndividualHoliday.CHRISTMAS) {
				this.holidayCalendar.individualChristmasEve = customEvent.detail.value;
				return;
			}

			if (customEvent.detail.type === IndividualHoliday.NEW_YEARS) {
				this.holidayCalendar.individualSilvester = customEvent.detail.value;
			}
		});
	}

	public resetHolidayCalendar() {
		this.setHolidayCalendar({
			id: null,
			calculator: "",
			description: "",
			version: null,
			generationMode: HolidayCalendarGenerationMode.OFF,
			individualChristmasEve: HolidayDayDuration.NONE,
			individualSilvester: HolidayDayDuration.NONE
		});
	}

	public setHolidayCalendar(holidayCalendar: HolidayCalendar) {
		// Copy HolidayCalendar state to not modify the original object
		this.holidayCalendar = copyOfObject(holidayCalendar);
		this.originalCalendarState = copyOfObject(holidayCalendar);
		this.fieldObserver.setModel(this.holidayCalendar);

		this.queryTemplateSettings();

		this.updateTitle();
		this.updateFieldValues();
	}

	private queryTemplateSettings() {
		if (!this.holidayCalendar.calculator) {
			this.calculatorInfo = null;
			this.updateFieldValues();
			return;
		}

		const saveButton = this.modal.getButtons("btn-primary");
		Button.setLoading(saveButton);

		api("admin/holidayCalculator.json")
			.get({
				calculatorBean: this.holidayCalendar.calculator
			})
			.done((response: HolidayCalculator) => {
				this.calculatorInfo = response;
				this.updateFieldValues();
			})
			.fail(() => {
				this.calculatorInfo = null;
				this.updateFieldValues();
			})
			.always(() => {
				Button.resetLoading(saveButton);
			})
	}

	private updateFieldValues() {
		this.modal.toggleButton("btn-outline-danger", this.holidayCalendar.id != null);
		$("#holidayCalendarId").val(this.holidayCalendar.id);
		$("#holidayCalendarDescription").val(this.holidayCalendar.description);
		$("#holidayCalendarCalculator").val(this.holidayCalendar.calculator);
		$("#holidayCalendarVersion").val(this.holidayCalendar.version);

		const isGenerationOn = this.holidayCalendar.generationMode === HolidayCalendarGenerationMode.ON;

		if (this.holidayCalendar.calculator) {
			$("#automaticGenerationToggleSection").show()
			$("#generationModeSwitch").prop("checked", isGenerationOn);
		} else {
			$("#automaticGenerationToggleSection").hide()
		}

		if (this.holidayCalendar.calculator && isGenerationOn) {
			$("#individualHolidaysSection").show();

			this.holidayIndividualDays.updateHoliday(IndividualHoliday.CHRISTMAS, {
				value: this.holidayCalendar.individualChristmasEve,
				include: this.calculatorInfo?.includesChristmasEve === true
			});
			this.holidayIndividualDays.updateHoliday(IndividualHoliday.NEW_YEARS, {
				value: this.holidayCalendar.individualSilvester,
				include: this.calculatorInfo?.includesNewYearsEve === true
			});
		} else {
			$("#individualHolidaysSection").hide();
		}
	}

	public open() {
		this.modal.show();
		$("#holidayCalendarDescription").focus();
	}

	public close() {
		this.modal.close();
	}

	private saveHolidayCalendar = (modal: BootstrapModal.Modal, button: JQuery) => {
		Button.setLoading(button);

		const save = () => {
			api("admin/saveHolidayCalendar.json")
				.post(this.holidayCalendar)
				.done(data => {
					modal.close();

					$(this).triggerHandler(HolidayCalendarDialogHandler.EVENT_HOLIDAY_CALENDAR_SAVED, data.holidayCalendar);
				})
				.always(() => {
					Button.resetLoading(button);
				});
		}

		const isExistingCalendar = this.holidayCalendar.id != null;

		const activatedAutomaticGeneration = this.originalCalendarState.generationMode !== HolidayCalendarGenerationMode.ON
			&& this.holidayCalendar.generationMode === HolidayCalendarGenerationMode.ON;

		const changedGenerationSettings = activatedAutomaticGeneration
			|| (this.originalCalendarState.calculator !== this.holidayCalendar.calculator)
			|| (this.originalCalendarState.individualChristmasEve !== this.holidayCalendar.individualChristmasEve)
			|| (this.originalCalendarState.individualSilvester !== this.holidayCalendar.individualSilvester);

		const deactivatedAutomaticGeneration = this.originalCalendarState.generationMode === HolidayCalendarGenerationMode.ON
			&& this.holidayCalendar.generationMode !== HolidayCalendarGenerationMode.ON;

		// Deactivation of automatic confirmation
		if (isExistingCalendar && deactivatedAutomaticGeneration) {
			BootstrapModal.confirm({
				title: "dialog.warning.title",
				message: messages["holidayCalendar.autoGeneration.deactivate.message"],
				yesLabel: "confirmation.ok",
				noLabel: "dialog.cancel",
				yesAction: (m) => {
					save();
					m.close();
				},
				noAction: (m) => {
					Button.resetLoading(button);
					m.close();
				}
			});
			return;
		}

		const isOnAfterSave = this.holidayCalendar.generationMode === HolidayCalendarGenerationMode.ON;

		// Preview and confirm regeneration when editing the template / settings
		if (isExistingCalendar && isOnAfterSave && changedGenerationSettings) {
			api("admin/previewHolidayCalculation.json")
				.get(this.holidayCalendar)
				.done((diff: HolidayGenerationDiffResponse | "") => {
					if (!diff || (diff.toUpdate.length == 0 && diff.toDelete.length == 0)) {
						// We only show the updated and deleted entries in the confirm dialog.
						// If there are no changes or the preview is empty because of an unsupported time range, then we do not to show the dialog
						save();
						return;
					}

					const message = this.buildRegenerationMessage(diff);
					BootstrapModal.confirm({
						title: "dialog.warning.title",
						message: message,
						destroyOnClose: true,
						escapeText: false, // allow HTML rendering in message
						yesLabel: "confirmation.ok",
						noLabel: "dialog.cancel",
						yesAction: (m) => {
							save();
							m.close();
						},
						noAction: (m) => {
							Button.resetLoading(button);
							m.close();
						}
					});
				})
				.fail(() => {
					// On failure to preview, fall back to save to not block
					save();
				});
		} else {
			save();
		}
	}

	private buildRegenerationMessage(diff: HolidayGenerationDiffResponse): string {
		const header = messages["holidayCalendar.regeneration.changedConfig.message"];

		const deleteList = diff.toDelete.map(h => `<li>${this.renderHolidayLine(h)}</li>`).join("");
		const updateList = diff.toUpdate.map((pair) => {
			const [existing, intended] = pair;
			return `<li>${formatDate(parseISODate(intended.date))}: ${this.renderHolidayText(existing)} → ${this.renderHolidayText(intended)}</li>`;
		}).join("");

		return `
				<div class="mt-10">
					<div>${header}</div>
					<div class="mt-10">
						<div class="mt-5" ${diff.toUpdate.length == 0 ? "style='display:none'" : ""}>
							<div><strong>${messages["holidayCalendar.regeneration.preview.update"]} (${diff.toUpdate.length})</strong></div>
							<ul>${updateList || "<li>–</li>"}</ul>
						</div>
						<div class="mt-5" ${diff.toDelete.length == 0 ? "style='display:none'" : ""}>
							<div><strong>${messages["holidayCalendar.regeneration.preview.delete"]} (${diff.toDelete.length})</strong></div>
							<ul>${deleteList || "<li>–</li>"}</ul>
						</div>
					</div>
				</div>
			`;
	}

	private renderHolidayLine(h: Holiday): string {
		return `${formatDate(parseISODate(h.date))}: ${this.renderHolidayText(h)}`;
	}

	private renderHolidayText(h: Holiday): string {
		const half = h.halfDay ? ` ${formatHalfDay(true)}` : "";
		return `${htmlEscape(h.description)} ${half}`;
	}

	private updateTitle() {
		const message = messages[this.holidayCalendar.id == null ? "holidayCalendar.header.create" : "holidayCalendar.header.edit"];

		this.modal.setTitle(message, true);
	}

	private setupEventHandlers() {
		this.fieldObserver.observeInput("#holidayCalendarDescription", "description");
		this.fieldObserver.observeInput("#holidayCalendarCalculator", "calculator");
		this.fieldObserver.observeInput("#generationModeSwitch", "generationMode", (selector) => $(selector).is(":checked") ? HolidayCalendarGenerationMode.ON : HolidayCalendarGenerationMode.OFF);

		this.fieldObserver.onFieldValueChange((e, data) => {
			if (data.field === "calculator") {
				if (!data.oldValue && !!data.newValue) {
					// If a calculator is selected and none was selected prior -> default to ON
					this.holidayCalendar.generationMode = HolidayCalendarGenerationMode.ON;
				} else if (!data.newValue) {
					// If no calculator is selected -> make sure that the generation mode is OFF as its an invalid combination
					this.holidayCalendar.generationMode = HolidayCalendarGenerationMode.OFF;
				}

				this.queryTemplateSettings();
			}

			if (data.field === "generationMode") {
				this.updateFieldValues();
			}
		});
	}
}

class CalculateHolidayDialogHandler {
	public static EVENT_HOLIDAYS_CALCULATED = "holidaysCalculated";
	private modal: BootstrapModal.Modal;

	constructor() {
		this.modal = new BootstrapModal.Modal({
			title: "calclulateHolidays.header",
			message: $("#calclulateHolidaysSelectionDialog"),
			destroyOnClose: false,
			buttons: [
				{
					text: "dialog.calculate",
					action: this.calculateHoliday
				},
				{
					text: "dialog.cancel"
				}
			]
		});
	}

	public calculateHolidaysFor(holidayCalendar: HolidayCalendar) {
		if (holidayCalendar.calculator) {
			$("#calculateHolidaysFor").val(holidayCalendar.calculator);
		}
		$("#calculateHolidaysHolidayCalendarId").val(holidayCalendar.id);

		this.modal.show();
	}

	private calculateHoliday = (modal: BootstrapModal.Modal, button: JQuery) => {
		Button.setLoading(button);

		api("admin/calculateHolidays.json")
			.postForm("#calclulateHolidaysSelectionForm")
			.done(() => {
				$(this).triggerHandler(CalculateHolidayDialogHandler.EVENT_HOLIDAYS_CALCULATED);

				Button.resetLoading(button);
				modal.close();
			});
	}

	public onHolidaysCalculated(action: () => void) {
		$(this).on(CalculateHolidayDialogHandler.EVENT_HOLIDAYS_CALCULATED, () => {
			action();
		});
	}
}
